import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { intgApi, issueUrl, partnerOf } from '../../utils/integrationsApi';
import { formatStamp } from '../../utils/datetime';
import { Sides, StatusPill } from './IntegrationsHome';
import '../../styles/Integrations.css';

/**
 * The run log (mockup screen 3): every item each run touched, newest first. This is also the
 * customer's evidence of what was copied where, so it shows before → after for each field.
 * Run log only — no email (D12).
 */
const FILTERS = [
  { key: '', label: 'All' },
  { key: 'created', label: 'Created' },
  { key: 'updated', label: 'Updated' },
  { key: 'warning', label: 'Warnings' },
  { key: 'error', label: 'Errors' },
  { key: 'unlinked', label: 'Unlinked' },
];

const ACTION_LABEL = {
  created: 'Created', updated: 'Updated', skipped: 'Skipped', warning: 'Warning', error: 'Error',
  unlinked: 'Unlinked', baseline: 'Switched on',
};

const TRIGGER_LABEL = { poll: 'Check', webhook: 'Webhook', retry: 'Retry', manual: 'Run now', enable: 'Switch on', test: 'Test' };

export default function RunLog() {
  const { id } = useParams();
  const [integ, setInteg] = useState(null);
  const [events, setEvents] = useState(null);
  const [runs, setRuns] = useState([]);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState({});
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    try {
      const [i, e, r] = await Promise.all([intgApi.get(id), intgApi.events(id, { action: filter }), intgApi.runs(id)]);
      setInteg(i);
      setEvents(e.events || []);
      setRuns(r.runs || []);
      setError('');
    } catch (e) { setError(e.message); }
  }, [id, filter]);

  useEffect(() => { load(); }, [load]);

  const flash = (t) => { setToast(t); setTimeout(() => setToast(''), 2500); };

  const runNow = async () => {
    try {
      const r = await intgApi.runNow(id);
      flash(r.message || 'Check started');
      setTimeout(load, 2000);
    } catch (e) { setError(e.message); }
  };

  const retry = async (ev) => {
    setBusy((b) => ({ ...b, [ev.id]: true }));
    try {
      const r = await intgApi.retry(ev.id);
      flash(r.ok ? 'Retried' : `Retry failed: ${r.error || 'see the newest entries'}`);
      load();
    } catch (e) { setError(e.message); }
    setBusy((b) => ({ ...b, [ev.id]: false }));
  };

  const runById = Object.fromEntries(runs.map((r) => [r.id, r]));
  const canEdit = integ?.can_edit;

  return (
    <div className="intg-page">
      <div className="intg-crumbs"><Link to={`/integrations?type=${integ?.connector || 'jira'}`}>{partnerOf(integ)} integrations</Link> / Run log</div>
      <div className="intg-head">
        <div>
          <h1>{integ?.name || 'Run log'}</h1>
          {integ && <Sides integ={integ} />}
        </div>
        <span className="intg-grow" />
        {integ && <StatusPill integ={integ} />}
        <button type="button" className="intg-btn" onClick={load}>Refresh</button>
        {canEdit && integ?.enabled && <button type="button" className="intg-btn intg-btn--primary" onClick={runNow}>Run now</button>}
      </div>

      {error && <div className="intg-note intg-note--bad" role="alert">{error}</div>}
      {integ?.enabled && integ.last_status === 'error' && integ.last_error && (
        <div className="intg-note intg-note--bad"><b>The last check failed:</b> {integ.last_error}</div>
      )}

      <div className="intg-chips" role="group" aria-label="Show">
        {FILTERS.map((f) => (
          <button key={f.key || 'all'} type="button" className="intg-chip" aria-pressed={filter === f.key}
                  onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
      </div>

      <div className="intg-table-wrap">
        <table className="intg-table">
          <thead>
            <tr>
              <th>Time ({'UTC'})</th><th>Trigger</th><th>Direction</th><th>Orcanos</th><th>{partnerOf(integ)}</th>
              <th>Result</th><th>Detail</th><th />
            </tr>
          </thead>
          <tbody>
            {events === null && <tr><td colSpan={8} className="intg-muted">Loading…</td></tr>}
            {events && events.length === 0 && (
              <tr><td colSpan={8} className="intg-muted">
                {filter ? 'Nothing of this kind yet.' : 'Nothing has run yet. Switch the integration on, or press Run now.'}
              </td></tr>
            )}
            {(events || []).map((ev) => {
              const run = runById[ev.run_id];
              const jiraUrl = ev.detail?.jira_url || issueUrl(integ, ev.jira_key);
              const orcUrl = ev.detail?.orcanos_url;
              return (
                <tr key={ev.id}>
                  <td className="intg-mono">{formatStamp(ev.created_at)}</td>
                  <td>{TRIGGER_LABEL[run?.trigger] || run?.trigger || ''}</td>
                  <td className="intg-mono">{ev.direction}</td>
                  <td className="intg-mono">{orcUrl ? <a href={orcUrl} target="_blank" rel="noreferrer">{ev.orcanos_id}</a> : ev.orcanos_id || '–'}</td>
                  <td className="intg-mono">{jiraUrl ? <a href={jiraUrl} target="_blank" rel="noreferrer">{ev.jira_key}</a> : ev.jira_key || '–'}</td>
                  <td className={`intg-act intg-act--${ev.action}`}>{ACTION_LABEL[ev.action] || ev.action}</td>
                  <td className="intg-detail">{ev.message}</td>
                  <td>
                    {canEdit && ev.action === 'error' && (ev.orcanos_id || ev.jira_issue_id) && (
                      <button type="button" className="intg-btn intg-btn--small" disabled={busy[ev.id]} onClick={() => retry(ev)}>
                        {busy[ev.id] ? 'Retrying…' : 'Retry'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {runs.length > 0 && (
        <details className="intg-runs">
          <summary>Checks ({runs.length})</summary>
          <div className="intg-table-wrap">
            <table className="intg-table">
              <thead><tr><th>Started (UTC)</th><th>Trigger</th><th>Status</th><th>Created</th><th>Updated</th><th>Warnings</th><th>Errors</th><th>Note</th></tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td className="intg-mono">{formatStamp(r.started_at)}</td>
                    <td>{TRIGGER_LABEL[r.trigger] || r.trigger}</td>
                    <td className={`intg-act intg-act--${r.status === 'ok' ? 'created' : r.status}`}>{r.status}</td>
                    <td className="intg-num">{r.n_created}</td>
                    <td className="intg-num">{r.n_updated}</td>
                    <td className="intg-num">{r.n_warn}</td>
                    <td className="intg-num">{r.n_err}</td>
                    <td className="intg-detail">{r.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      {toast && <div className="intg-toast" role="status">{toast}</div>}
    </div>
  );
}
